/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface CollectionDescriptionEditorProps {
  collectionHandle: string;
  initialDescription: string;
  onUpdate?: () => void;
}

const CollectionDescriptionEditor: React.FC<CollectionDescriptionEditorProps> = ({
  collectionHandle,
  initialDescription,
  onUpdate
}) => {
  const [description, setDescription] = useState(initialDescription);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Sync internal state when initialDescription changes
  useEffect(() => {
    setDescription(initialDescription);
  }, [initialDescription]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/update-collection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collectionHandle,
          description,
          token,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update collection');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update collection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editor-section">
      <h3>Collection Description</h3>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        disabled={saving}
        className="description-textarea"
        placeholder="Enter your collection description..."
      />
      
      <button 
        onClick={handleSave} 
        disabled={saving || description === initialDescription}
        className="save-btn"
      >
        {saving ? 'Saving...' : 'Save Description'}
      </button>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">✓ Description updated successfully!</div>}
    </div>
  );
};

export default CollectionDescriptionEditor;